import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import { Avatar, Box, Card, CardContent, CircularProgress, Pagination, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getPatientQueue, PatientQueueItem } from '../../api/api';
import { useApiClients } from '../../hooks/useAppClients';

const PATIENTS_PER_PAGE = 4;

interface PatientQueueWidgetProps {
  practitionerId?: string;
}

export function PatientQueueWidget({ practitionerId }: PatientQueueWidgetProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { oystehr } = useApiClients();
  const [currentPage, setCurrentPage] = useState(1);
  const [patients, setPatients] = useState<PatientQueueItem[]>([]);
  const [totalPatients, setTotalPatients] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPatients = async (): Promise<void> => {
      // oystehr can be null/undefined initially or if not authenticated
      if (!oystehr) return;

      setLoading(true);
      setError(null);
      try {
        const response = await getPatientQueue(oystehr, {
          page: currentPage,
          limit: PATIENTS_PER_PAGE,
          practitioner: practitionerId,
        });
        console.log('API Response for Patient Queue:', response);
        setPatients(response.patients);
        setTotalPatients(response.total);
      } catch (err: any) {
        console.error('Error fetching patient queue:', err);
        setError('Failed to load patients');
        // Fallback to empty list or handle gracefully
        setPatients([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchPatients();
  }, [oystehr, currentPage, practitionerId]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number): void => {
    setCurrentPage(page);
  };

  const handlePatientClick = (patientId: string): void => {
    navigate(`/patient/${patientId}/info`);
  };

  const totalPages = Math.ceil(totalPatients / PATIENTS_PER_PAGE) || 1;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="h2" fontWeight="600">
            {t('home.patientQueue.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading...' : t('home.patientQueue.activePatients', { count: totalPatients })}
          </Typography>
        </Box>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
              color: 'error.main',
            }}
          >
            <Typography>{error}</Typography>
          </Box>
        ) : patients.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
              color: 'text.secondary',
            }}
          >
            <Typography>No patients found</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {patients.map((patient: PatientQueueItem, index: number) => (
              <Box
                key={patient.id}
                onClick={() => handlePatientClick(patient.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  py: 2.5,
                  px: 2,
                  cursor: 'pointer',
                  borderBottom: index < patients.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {/* Avatar */}
                <Avatar
                  sx={{
                    bgcolor: '#EEEEF2',
                    color: '#22222d',
                    width: 56,
                    height: 56,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 28 }} />
                </Avatar>

                {/* Patient Info */}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ fontSize: '1rem', mb: 0.5 }}>
                    {patient.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    {patient.height || '--'}, {patient.weight || '--'} • {patient.age} years
                  </Typography>
                </Box>

                {/* Arrow Icon */}
                <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      {/* Pagination */}
      {!loading && totalPatients > 0 && (
        <Box
          sx={{
            p: 2.5,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '0.875rem',
              },
            }}
          />
        </Box>
      )}
    </Card>
  );
}
